import { useCallback, useEffect, useRef, useState } from "react";
import { notification } from "antd";
import type { FormInstance } from "antd/es/form";
import { TestHttpService } from "@/shared/services/tests/test-http.service";
import { NormsHttpService } from "@/shared/services/norms/norms-http.service";
import { getFriendlyErrorMessage } from "@/shared/services/http";
import type { ITest } from "@/modules/assessments/types/test.interface";
import type { INorm } from "@/shared/services/norms/norms-http.service";
import type { IScale } from "@/modules/assessments/types/scale.interface";
import { findNormMatchingTemplateName } from "./findNormMatchingTemplateName";
import type { SelectedTest, TemplateFormData } from "./types";

interface TestDetails {
  test: ITest;
  norms: INorm[];
  scales: IScale[];
}

export interface TemplateTestDetails {
  details: Record<number, TestDetails>;
  loadingIds: Set<number>;
  fetchTest: (testId: number) => Promise<void>;
}

export function useTemplateTestDetails(form: FormInstance<TemplateFormData>): TemplateTestDetails {
  const [details, setDetails] = useState<Record<number, TestDetails>>({});
  const [loadingIds, setLoadingIds] = useState<Set<number>>(() => new Set());

  // Keep a ref of the latest details so fetchTest stays stable
  // and doesn't trigger consumers' effects re-running every time `details` changes.
  const detailsRef = useRef(details);
  useEffect(() => {
    detailsRef.current = details;
  }, [details]);

  const fetchTest = useCallback(
    async (testId: number) => {
      let entry = detailsRef.current[testId];

      if (!entry) {
        setLoadingIds((prev) => new Set(prev).add(testId));
        try {
          const [test, norms, scales] = await Promise.all([
            TestHttpService.getTestById(testId),
            NormsHttpService.getTestNorms(testId),
            TestHttpService.getSpecificTestScales(testId),
          ]);
          entry = { test, norms, scales };
          setDetails((prev) => ({ ...prev, [testId]: entry }));
        } catch (error) {
          const message = getFriendlyErrorMessage(error) ?? "Failed to load test details. Please try again.";
          notification.error({ message });
          return;
        } finally {
          setLoadingIds((prev) => {
            const next = new Set(prev);
            next.delete(testId);
            return next;
          });
        }
      }

      autoSelectTemplateNorm(form, testId, entry.norms);
    },
    [form],
  );

  // The `tests` Form.List rules read `details`, but antd validates them when the list
  // value changes — which happens before a newly added test has been fetched. Re-validate
  // whenever `details` arrives so those rules see it. The empty check skips the initial
  // mount so an untouched form doesn't open with errors already showing.
  useEffect(() => {
    if (Object.keys(details).length === 0) return;

    form.validateFields([["tests"]]);
  }, [details, form]);

  return { details, loadingIds, fetchTest };
}

function autoSelectTemplateNorm(form: FormInstance<TemplateFormData>, testId: number, norms: INorm[]) {
  const tests: SelectedTest[] = form.getFieldValue("tests") ?? [];
  const index = tests.findIndex((test) => test.testId === testId);
  if (index === -1) return;
  if (tests[index].normId) return;

  const matchingNorm = findNormMatchingTemplateName(form.getFieldValue("templateName"), norms);
  if (!matchingNorm) return;

  form.setFields([{ name: ["tests", index, "normId"], value: matchingNorm.normId }]);
}
